import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import axios from 'axios';
import { toast } from 'react-toastify';
import { useShallow } from 'zustand/react/shallow';

import { SuccessToast } from '@src/components';

import { debounce, formatDateTime, getTimezonesWithOffset } from '@src/utils';

import { useCtaStore, useRoomStore } from '@src/stores';

import { CURRENT_TIME_ZONE, WEBINAR_DURATION_VALUES } from '@src/constants/webinar.constants';
import { useFetchCTAs } from '@src/hooks/queries/useWebinarQuery';

import { IWebinar, TWebinarFormState } from '../types';
import { sendEventToWebView } from '@src/bridge/bridgeMessenger';
import { EWebViewEvents } from '@src/bridge/types';
import useSendPageFinished from '@src/hooks/useSendPageFinished';

// RoomId will be required if we are in edit mode
const useWebinarForm = () => {
  const { roomTitle, setRoomTitle } = useRoomStore(
    useShallow((state) => ({
      setRoomTitle: state.setRoomTitle,
      roomTitle: state.roomTitle,
    }))
  );
  const { isCreateCtaModalOpen, setCtas, setIsCtaManagementModalOpen } = useCtaStore(
    useShallow((state) => ({
      isCreateCtaModalOpen: state.isCreateCtaModalOpen,
      setCtas: state.setCtas,
      setIsCtaManagementModalOpen: state.setIsCtaManagementModalOpen,
    }))
  );

  useSendPageFinished({ isMeetingEnded: false });

  const navigate = useNavigate();
  const durationValues = WEBINAR_DURATION_VALUES;
  const [isLoading, setIsLoading] = useState(false);
  const [isTimeZoneModalOpen, setIsTimeZoneModalOpen] = useState(false);
  const [originalScheduledDate, setOriginalScheduledDate] = useState<Date | null>(null);
  const [isForbidden, setIsForbidden] = useState(false);

  const [params] = useSearchParams();
  const roomId = params.get('wid');
  const [formState, setFormState] = useState<TWebinarFormState>({
    title: roomTitle,
    scheduledDate: null,
    durationValue: '15',
    questionsEnabled: false,
    requireManualApproval: false,
    requireRegistration: false,
    expiresAfterEnd: false,
    registerExpireDate: null,
    timeZone: CURRENT_TIME_ZONE,
    reminders: [] as (Date | null)[],
    maxQuestions: 1,
    allowAnonymous: false,
    allowDismissQuestion: true,
    maxThreadReplies: 10,
    ctaEnabled: true,
    calculatorEnabled: false,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isQuestionsModalOpened, setIsQuestionsModalOpened] = useState(false);
  const [isMaxLimitEnabled, setIsMaxLimitEnabled] = useState(false);
  const backupQuestionSettings = useRef<any>({});
  const { refetch: refetchCTAs } = useFetchCTAs(roomId ?? undefined, false);

  const handleOpenCTAManagement = useCallback(async () => {
    if (!formState.ctaEnabled) return;

    setIsCtaManagementModalOpen(true);

    // Create mode: BE might not support CTAs without a roomId yet.
    if (!roomId) {
      setCtas([]);
      return;
    }

    const result = await refetchCTAs();

    if (result.isError) {
      console.error(result.error);
    }

    setCtas(result.data?.ctas ?? []);
  }, [formState.ctaEnabled, refetchCTAs, roomId, setIsCtaManagementModalOpen, setCtas]);

  const [selectedTimeZone, setSelectedTimeZone] = useState('');
  const timeZones = useMemo(() => getTimezonesWithOffset(), []);
  const formattedTime = useMemo(() => {
    if (!formState.scheduledDate || !formState.durationValue) {
      return '';
    }
    const startTime = formState.scheduledDate;
    const parsedDuration = parseInt(formState.durationValue || '', 10);
    const durationInSeconds = isNaN(parsedDuration) || parsedDuration <= 0 ? 15 * 60 : parsedDuration * 60;
    const endTime = new Date(formState.scheduledDate.getTime() + durationInSeconds * 1000);
    const timeZoneValue =
      formState.timeZone !== CURRENT_TIME_ZONE
        ? timeZones.find(({ value }) => value === formState.timeZone)?.offsetValue
        : null;
    return `Webinar time from ${formatDateTime(startTime)} - ${formatDateTime(endTime)}${timeZoneValue ? ` ${timeZoneValue} UTC time` : ''}`;
  }, [formState, timeZones]);

  useEffect(() => {
    setSelectedTimeZone(formState.timeZone);
  }, [formState.timeZone]);

  useEffect(() => {
    if (isForbidden || !roomId || !originalScheduledDate) return;

    const diff = originalScheduledDate.getTime() - 20 * 60 * 1000 - Date.now();

    if (diff > 0) {
      const timeout = setTimeout(() => {
        setIsForbidden(true);
      }, diff);
      return () => clearTimeout(timeout);
    }

    setIsForbidden(true);
  }, [roomId, originalScheduledDate, isForbidden]);

  const initEditForm = useCallback(async () => {
    setIsLoading(true);
    try {
      const request = await axios.get(`${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/room?roomId=${roomId}`, {
        withCredentials: true,
      });
      const webinar: IWebinar = request.data?.[0];

      const webinarStartTime = new Date(webinar.startTime);
      setOriginalScheduledDate(webinarStartTime);

      setFormState((res) => ({
        ...res,
        title: webinar.roomTitle,
        scheduledDate: webinarStartTime,
        durationValue: (
          (new Date(webinar.endTime).getTime() - new Date(webinar.startTime).getTime()) /
          60000
        ).toString(),

        questionsEnabled: webinar.roomSetting?.questionSettings?.questionsEnabled ?? false,
        maxQuestions: webinar.roomSetting?.questionSettings?.questionsEnabled
          ? webinar.roomSetting?.questionSettings?.maxQuestions || 1
          : 1,
        allowAnonymous: webinar.roomSetting?.questionSettings?.allowAnonymous ?? false,
        maxThreadReplies: webinar.roomSetting?.questionSettings?.maxThreadReplies ?? 10,
        allowDismissQuestion: webinar.roomSetting?.questionSettings?.allowDismissQuestion ?? true,
        requireRegistration: !!webinar.roomSetting?.registrationRequired,
        expiresAfterEnd: !!webinar.roomSetting?.registrationExpiryDate,
        registerExpireDate: webinar.roomSetting?.registrationExpiryDate
          ? new Date(webinar.roomSetting?.registrationExpiryDate)
          : null,
        timeZone: webinar.roomSetting?.timeZone || CURRENT_TIME_ZONE,
        reminders: webinar.roomSetting?.reminders?.map((r) => new Date(r!)) ?? [],
        ctaEnabled: webinar.roomSetting?.ctaSettings?.ctaEnabled ?? true,
        calculatorEnabled: webinar.roomSetting?.calculatorSettings?.shareCalculatorEnabled ?? false,
      }));
      setIsMaxLimitEnabled((webinar.roomSetting?.questionSettings?.maxQuestions || 1) > 1);
    } catch (e) {}
    setIsLoading(false);
  }, [roomId]);

  useEffect(() => {
    if (roomId) {
      initEditForm();
    }
  }, [initEditForm, roomId]);

  const updateFormField = <K extends keyof TWebinarFormState>(key: K, value: TWebinarFormState[K]) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  };

  const buildPayload = useCallback(() => {
    const startDate = formState.scheduledDate ?? null;

    const parsedDuration = parseInt(formState.durationValue || '', 10);
    const durationInSeconds = isNaN(parsedDuration) || parsedDuration <= 0 ? 15 * 60 : parsedDuration * 60;

    const startTimeISO = startDate ? startDate.toISOString() : '';
    const endTimeISO = startDate ? new Date(startDate.getTime() + durationInSeconds * 1000).toISOString() : '';

    const now = new Date();
    const registerExpireDate = formState.registerExpireDate ?? null;

    const isValidExpiry =
      formState.expiresAfterEnd &&
      registerExpireDate &&
      startDate &&
      registerExpireDate > now &&
      registerExpireDate < startDate;

    const registerExpiryISO = isValidExpiry ? registerExpireDate.toISOString() : null;

    return {
      room_title: formState.title.trim(),
      start_time: startTimeISO,
      end_time: endTimeISO,
      roomSetting: {
        registrationRequired: formState.requireRegistration,
        registrationExpiryDate: registerExpiryISO,
        timeZone: formState.timeZone,
        reminders: (formState.reminders ?? []).map((date) => date?.toISOString()).filter(Boolean),

        questionSettings: {
          questionsEnabled: formState.questionsEnabled,
          ...(formState.questionsEnabled && {
            maxQuestions: formState.maxQuestions || 1,
            allowAnonymous: formState.allowAnonymous,
            allowDismissQuestion: formState.allowDismissQuestion,
            maxThreadReplies: formState.maxThreadReplies,
          }),
        },
        ctaSettings: {
          ctaEnabled: formState.ctaEnabled,
        },
        calculatorSettings: {
          shareCalculatorEnabled: formState.calculatorEnabled,
        },
      },
    };
  }, [formState]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const onDiscard = (event: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    navigate('/');
  };

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};
    const now = new Date();

    if (!formState.title.trim()) {
      newErrors.title = 'Webinar title is required.';
    } else if (formState.title.trim().length < 4) {
      newErrors.title = 'Webinar title must be at least 4 characters.';
    }

    const startDate = formState.scheduledDate;
    if (!startDate) {
      newErrors.scheduledDate = 'Webinar date and time is required.';
    } else if (startDate.getTime() <= now.getTime()) {
      newErrors.scheduledDate = 'Webinar date must be in the future.';
    }

    const reminders = formState.reminders ?? [];
    if (reminders.length > 0) {
      for (let i = 0; i < reminders.length; i++) {
        const reminderValue = reminders[i];
        if (!reminderValue) {
          newErrors[`reminders_${i}`] = 'Reminder is required.';
          continue;
        }

        const reminder = new Date(reminderValue);

        if (reminder.getTime() <= now.getTime()) {
          newErrors[`reminders_${i}`] = 'Reminder must be in the future.';
        }

        if (startDate && reminder.getTime() >= startDate.getTime()) {
          newErrors[`reminders_${i}`] = 'Reminder must be before the webinar start time.';
        }

        if (reminders.length === 2 && startDate) {
          const diffInMinutes = (startDate.getTime() - reminder.getTime()) / 60000;
          if (diffInMinutes < 20) {
            newErrors[`reminders_${i}`] = 'Reminder must be at least 20 minutes before webinar starts.';
          }
        }
      }
      const toMinuteUnit = (d: Date): number => Math.floor(d.getTime() / 60000);

      if (reminders.length === 2 && reminders[0] && reminders[1]) {
        const [first, second] = reminders.map((r) => toMinuteUnit(new Date(r!)));
        if (first === second) {
          newErrors['reminders_1'] = 'Reminders must have different timestamps';
        }
      }
    }

    if (formState.expiresAfterEnd) {
      const expiry = formState.registerExpireDate;
      if (!expiry) {
        newErrors.registerExpireDate = 'Expiry date is required.';
      } else {
        if (expiry.getTime() <= now.getTime()) {
          newErrors.registerExpireDate = 'Expiry date must be in the future.';
        }
        if (startDate && expiry.getTime() >= startDate.getTime()) {
          newErrors.registerExpireDate = 'Expiry must be before webinar start time.';
        }
      }
    }

    if (formState.maxQuestions > 10) {
      newErrors.maxQuestions = 'The max allowed number of questions per viewer is 10 questions';
    }
    return newErrors;
  }, [formState]);

  const handleMaxQuestionsToggle = (isOn: boolean) => {
    if (!formState.questionsEnabled) return;
    setIsMaxLimitEnabled(isOn);
    updateFormField('maxQuestions', 1);
    removeFormError('maxQuestions');
  };

  const onQuestionsCancel = () => {
    const backup = backupQuestionSettings.current;
    if (backup) {
      updateFormField('questionsEnabled', backup.questionsEnabled);
      updateFormField('maxQuestions', backup.maxQuestions);
      updateFormField('allowAnonymous', backup.allowAnonymous);
      updateFormField('allowDismissQuestion', backup.allowDismissQuestion);
      updateFormField('maxThreadReplies', backup.maxThreadReplies);
      setIsMaxLimitEnabled(backup.isMaxLimitEnabled);
    }
    removeFormError('maxQuestions');
    setIsQuestionsModalOpened(false);
  };

  const onQuestionsDone = () => {
    if (formErrors.maxQuestions) {
      return; //
    }
    setIsQuestionsModalOpened(false);
  };

  const removeFormError = (field: string) => {
    setFormErrors((prevErrors) => {
      const { [field]: _, ...rest } = prevErrors;
      return rest;
    });
  };

  const apiCall = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = buildPayload();
      if (roomId) {
        await axios.put(
          `${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/room`,
          { ...payload, id: roomId },
          {
            withCredentials: true,
          }
        );
        sendEventToWebView({ eventType: EWebViewEvents.CLOSE });
        navigate('/');
        // we need to wait until its finished navigation.
        setTimeout(() => {
          toast(<SuccessToast message="Webinar meeting has been updated successfully!" />, {
            className: 'custom-toast-wrapper',
            autoClose: 3000,
            closeButton: false,
            hideProgressBar: true,
            icon: false,
          });
        }, 100);
      } else {
        await axios.post(`${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/room`, payload, {
          withCredentials: true,
        });
        // reset the title as we already created a webinar
        setRoomTitle('');
        navigate('/');
        sendEventToWebView({ eventType: EWebViewEvents.CLOSE });
        // we need to wait until its finished navigation.
        setTimeout(() => {
          toast(<SuccessToast message="Webinar meeting has been created successfully!" />, {
            className: 'custom-toast-wrapper',
            autoClose: 3000,
            closeButton: false,
            hideProgressBar: true,
            icon: false,
          });
        }, 100);
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 'FORBIDDEN') {
        setIsForbidden(true);
        toast.error(error?.response?.data?.error, {
          icon: false,
        });
      }
    }
    setIsLoading(false);
  }, [buildPayload, navigate, roomId, setRoomTitle]);

  const debouncedApiCall = useMemo(() => debounce(apiCall, 300), [apiCall]);

  const debouncedSubmitFunction = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      debouncedApiCall.cancel?.();
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setFormErrors(validationErrors);
        setIsLoading(false);
        return;
      }
      debouncedApiCall();
    },
    [debouncedApiCall, validateForm]
  );

  return {
    updateFormField,
    debouncedSubmitFunction,
    onDiscard,
    removeFormError,
    handleKeyDown,
    formState,
    isDateEditable: !isForbidden,
    durationValues,
    formErrors,
    isLoading,
    isTimeZoneModalOpen,
    setIsTimeZoneModalOpen,
    selectedTimeZone,
    setSelectedTimeZone,
    formattedTime,
    timeZones,
    validateForm,
    isMaxLimitEnabled,
    setIsMaxLimitEnabled,
    isQuestionsModalOpened,
    setIsQuestionsModalOpened,
    backupQuestionSettings,
    setFormErrors,
    isCreateCtaModalOpen,
    handleMaxQuestionsToggle,
    onQuestionsCancel,
    onQuestionsDone,
    handleOpenCTAManagement,
    isForbidden,
  };
};
export default useWebinarForm;
